// Members-page strings, externalized for future i18n. The widget owns the
// dictionary because it composes every entity/feature component that
// renders text. Pages/members pulls from this same module so a single edit
// propagates.
//
// Empty-state copy is per-stage so toggling a single chip surfaces a
// stage-appropriate message ("No AT_RISK members — great news!" vs
// "No CHURNED members yet — they'll appear here once activity is silent
// for 30+ days"). The "no chips selected" branch falls back to the
// generic empty body.

import type { MemberLifecycleStage } from "@community-analytics/shared";

export const MEMBERS_STRINGS = {
  pageTitle: "Members",
  loading: "Loading members…",
  filterAria: "Filter by lifecycle stage",
  loadMore: "Load more",
  loadingMore: "Loading…",
  unknownName: "Anonymous member",
  lurkerLabel: "Joined, never messaged",
  errorTitle: "We couldn't load your members",
  errorBodyDefault: "Something went wrong fetching the member list.",
  retry: "Retry",
  noCommunity: {
    title: "No community connected yet",
    body: "Open the bot in Telegram and follow the prompts to add your first community.",
  },
  empty: {
    title: "No members match your filters",
    body: "Try toggling different stage chips, or wait for new activity.",
  },
  upgrade: {
    title: "Upgrade to Pro to see individual members",
    body: "FREE plan shows per-stage totals. Pro unlocks the full member list with filtering, infinite scroll, and per-row context.",
    cta: "Upgrade to Pro",
  },
  stageLabels: {
    NEW: "New",
    ACTIVE: "Active",
    ENGAGED: "Engaged",
    DORMANT: "Dormant",
    AT_RISK: "At risk",
    CHURNED: "Churned",
  } satisfies Record<MemberLifecycleStage, string>,
  emptyByStage: {
    NEW: "No NEW members yet — share an invite link to get started.",
    ACTIVE: "No ACTIVE members yet — they'll appear after their first message.",
    ENGAGED:
      "No ENGAGED members yet — keep posting; engagement builds over time.",
    DORMANT: "No DORMANT members — your community is buzzing.",
    AT_RISK: "No AT_RISK members — great news!",
    CHURNED: "No CHURNED members yet.",
  } satisfies Record<MemberLifecycleStage, string>,
} as const;

export type MembersStrings = typeof MEMBERS_STRINGS;
